/**
 * Hooks TanStack Query — catalogue paramétrable (formats × framings × supports).
 *
 * Trois entités symétriques alimentant les variantes (cf. S2.5b
 * `work_variants`). Page `/settings` permet à Stéphanie de peupler son
 * catalogue ; les pages NewWork / WorkDetail consomment ces hooks pour
 * sourcer les selects de variantes.
 *
 * Pattern aligné sur `useWorks.ts` — query keys hiérarchiques, mutations
 * invalident la liste correspondante.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { catalogApi } from '@/utils/api/catalog'
import type {
  EmotionalIntentCreateRequest,
  EmotionalIntentResponse,
  EmotionalIntentUpdateRequest,
  FormatCreateRequest,
  FormatResponse,
  FormatUpdateRequest,
  FramingCreateRequest,
  FramingResponse,
  FramingUpdateRequest,
  SlugSuggestRequest,
  SlugSuggestResponse,
  SupportCreateRequest,
  SupportResponse,
  SupportUpdateRequest,
  TargetAudienceCreateRequest,
  TargetAudienceResponse,
  TargetAudienceUpdateRequest,
} from '@/types/catalog'

// ──────────────────────────────────────────────────────────────────────────────
// Query keys
// ──────────────────────────────────────────────────────────────────────────────

interface ListParams {
  only_active?: boolean
}

export const catalogKeys = {
  formats: {
    all: ['catalog', 'formats'] as const,
    list: (params: ListParams) => ['catalog', 'formats', 'list', params] as const,
  },
  framings: {
    all: ['catalog', 'framings'] as const,
    list: (params: ListParams) => ['catalog', 'framings', 'list', params] as const,
  },
  supports: {
    all: ['catalog', 'supports'] as const,
    list: (params: ListParams) => ['catalog', 'supports', 'list', params] as const,
  },
  emotionalIntents: {
    all: ['catalog', 'emotional-intents'] as const,
    list: (params: ListParams) => ['catalog', 'emotional-intents', 'list', params] as const,
  },
  targetAudiences: {
    all: ['catalog', 'target-audiences'] as const,
    list: (params: ListParams) => ['catalog', 'target-audiences', 'list', params] as const,
  },
}

// ──────────────────────────────────────────────────────────────────────────────
// Formats
// ──────────────────────────────────────────────────────────────────────────────

export function useFormats(params: ListParams = {}) {
  return useQuery<FormatResponse[]>({
    queryKey: catalogKeys.formats.list(params),
    queryFn: () => catalogApi.listFormats(params),
  })
}

export function useCreateFormat() {
  const qc = useQueryClient()
  return useMutation<FormatResponse, { detail: string; status?: number }, FormatCreateRequest>({
    mutationFn: (payload) => catalogApi.createFormat(payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: catalogKeys.formats.all })
    },
  })
}

export function useUpdateFormat() {
  const qc = useQueryClient()
  return useMutation<
    FormatResponse,
    { detail: string; status?: number },
    { id: number; payload: FormatUpdateRequest }
  >({
    mutationFn: ({ id, payload }) => catalogApi.updateFormat(id, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: catalogKeys.formats.all })
    },
  })
}

export function useDeleteFormat() {
  const qc = useQueryClient()
  return useMutation<void, { detail: string; status?: number }, number>({
    mutationFn: (id) => catalogApi.deleteFormat(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: catalogKeys.formats.all })
    },
  })
}

// ──────────────────────────────────────────────────────────────────────────────
// Framings
// ──────────────────────────────────────────────────────────────────────────────

export function useFramings(params: ListParams = {}) {
  return useQuery<FramingResponse[]>({
    queryKey: catalogKeys.framings.list(params),
    queryFn: () => catalogApi.listFramings(params),
  })
}

export function useCreateFraming() {
  const qc = useQueryClient()
  return useMutation<FramingResponse, { detail: string; status?: number }, FramingCreateRequest>({
    mutationFn: (payload) => catalogApi.createFraming(payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: catalogKeys.framings.all })
    },
  })
}

export function useUpdateFraming() {
  const qc = useQueryClient()
  return useMutation<
    FramingResponse,
    { detail: string; status?: number },
    { id: number; payload: FramingUpdateRequest }
  >({
    mutationFn: ({ id, payload }) => catalogApi.updateFraming(id, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: catalogKeys.framings.all })
    },
  })
}

export function useDeleteFraming() {
  const qc = useQueryClient()
  return useMutation<void, { detail: string; status?: number }, number>({
    mutationFn: (id) => catalogApi.deleteFraming(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: catalogKeys.framings.all })
    },
  })
}

// ──────────────────────────────────────────────────────────────────────────────
// Supports
// ──────────────────────────────────────────────────────────────────────────────

export function useSupports(params: ListParams = {}) {
  return useQuery<SupportResponse[]>({
    queryKey: catalogKeys.supports.list(params),
    queryFn: () => catalogApi.listSupports(params),
  })
}

export function useCreateSupport() {
  const qc = useQueryClient()
  return useMutation<SupportResponse, { detail: string; status?: number }, SupportCreateRequest>({
    mutationFn: (payload) => catalogApi.createSupport(payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: catalogKeys.supports.all })
    },
  })
}

export function useUpdateSupport() {
  const qc = useQueryClient()
  return useMutation<
    SupportResponse,
    { detail: string; status?: number },
    { id: number; payload: SupportUpdateRequest }
  >({
    mutationFn: ({ id, payload }) => catalogApi.updateSupport(id, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: catalogKeys.supports.all })
    },
  })
}

export function useDeleteSupport() {
  const qc = useQueryClient()
  return useMutation<void, { detail: string; status?: number }, number>({
    mutationFn: (id) => catalogApi.deleteSupport(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: catalogKeys.supports.all })
    },
  })
}

// ──────────────────────────────────────────────────────────────────────────────
// Emotional intents (Tier 2 — paramétrable S2.5c)
// ──────────────────────────────────────────────────────────────────────────────

export function useEmotionalIntents(params: ListParams = {}) {
  return useQuery<EmotionalIntentResponse[]>({
    queryKey: catalogKeys.emotionalIntents.list(params),
    queryFn: () => catalogApi.listEmotionalIntents(params),
  })
}

export function useCreateEmotionalIntent() {
  const qc = useQueryClient()
  return useMutation<
    EmotionalIntentResponse,
    { detail: string; status?: number },
    EmotionalIntentCreateRequest
  >({
    mutationFn: (payload) => catalogApi.createEmotionalIntent(payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: catalogKeys.emotionalIntents.all })
    },
  })
}

export function useUpdateEmotionalIntent() {
  const qc = useQueryClient()
  return useMutation<
    EmotionalIntentResponse,
    { detail: string; status?: number },
    { id: number; payload: EmotionalIntentUpdateRequest }
  >({
    mutationFn: ({ id, payload }) => catalogApi.updateEmotionalIntent(id, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: catalogKeys.emotionalIntents.all })
    },
  })
}

export function useDeleteEmotionalIntent() {
  const qc = useQueryClient()
  return useMutation<void, { detail: string; status?: number }, number>({
    mutationFn: (id) => catalogApi.deleteEmotionalIntent(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: catalogKeys.emotionalIntents.all })
    },
  })
}

// ──────────────────────────────────────────────────────────────────────────────
// Target audiences (Tier 2 — paramétrable S2.5c)
// ──────────────────────────────────────────────────────────────────────────────

export function useTargetAudiences(params: ListParams = {}) {
  return useQuery<TargetAudienceResponse[]>({
    queryKey: catalogKeys.targetAudiences.list(params),
    queryFn: () => catalogApi.listTargetAudiences(params),
  })
}

export function useCreateTargetAudience() {
  const qc = useQueryClient()
  return useMutation<
    TargetAudienceResponse,
    { detail: string; status?: number },
    TargetAudienceCreateRequest
  >({
    mutationFn: (payload) => catalogApi.createTargetAudience(payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: catalogKeys.targetAudiences.all })
    },
  })
}

export function useUpdateTargetAudience() {
  const qc = useQueryClient()
  return useMutation<
    TargetAudienceResponse,
    { detail: string; status?: number },
    { id: number; payload: TargetAudienceUpdateRequest }
  >({
    mutationFn: ({ id, payload }) => catalogApi.updateTargetAudience(id, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: catalogKeys.targetAudiences.all })
    },
  })
}

export function useDeleteTargetAudience() {
  const qc = useQueryClient()
  return useMutation<void, { detail: string; status?: number }, number>({
    mutationFn: (id) => catalogApi.deleteTargetAudience(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: catalogKeys.targetAudiences.all })
    },
  })
}

// ──────────────────────────────────────────────────────────────────────────────
// Slug suggestion (Claude)
// ──────────────────────────────────────────────────────────────────────────────

export function useSuggestSlug() {
  return useMutation<SlugSuggestResponse, { detail: string; status?: number }, SlugSuggestRequest>({
    mutationFn: (payload) => catalogApi.suggestSlug(payload),
  })
}
